import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  catalogCategorySchema,
  catalogCustomFieldSchema,
  catalogCycleSchema,
  catalogProductSchema,
  type CatalogEntry,
  type CatalogOrderList,
  type CatalogProduct,
  type CatalogSettings,
} from "@school-sales-ai/shared";
import { ApiError } from "@/lib/api";
import { catalogRowApi, type CatalogRowValue } from "@/hooks/useCatalog";

/**
 * The catalog editor's state — and the reason there is no «Guardar» button.
 *
 * Every edit is saved on its own, the moment it is made, through the per-row
 * endpoints. A batch save the admin could forget to press was losing real
 * changes, and the version it produced protected nothing (the agent reads the
 * live tables). Four layers make sure the LAST edit is never the one lost:
 *
 *  1. discrete controls (selects, dates, checkboxes) save on change;
 *  2. text saves `AUTOSAVE_DEBOUNCE_MS` after the last keystroke, and at once on
 *     blur or Enter — never one request per key;
 *  3. navigating away inside the CRM flushes first (`flushAll`, used by the
 *     page's blocker);
 *  4. hiding the tab (closing it, switching apps on a phone) flushes with
 *     `keepalive`, so the request outlives the page.
 *
 * Each row saves through its own queue: an edit made while the row's create is
 * still in flight waits for the id and then patches, and two saves of one row
 * can never land out of order.
 */

export type CatalogList = CatalogOrderList;

export const AUTOSAVE_DEBOUNCE_MS = 2000;

/** Saves chained onto one call for edits typed while a request was in flight. */
const MAX_FOLLOW_UP_SAVES = 3;

export type RowStatus = "idle" | "saving" | "saved" | "error";

export type EditorRow<L extends CatalogList> = {
  /** Stable across saves and reorders — the React key, never the index. */
  key: string;
  /** Null until the row has been created on the server. */
  id: string | null;
  /** What the server holds, or null for a row not created yet. */
  saved: CatalogRowValue[L] | null;
  /** What the inputs show. */
  current: CatalogRowValue[L];
  status: RowStatus;
  /** The server's own words for the last failed save. */
  error: string | null;
  /** The server's error code, when the row reacts to it (a «Reactivar» button). */
  errorCode: string | null;
  /** Validation messages are shown once the admin has tried to leave a field. */
  revealed: boolean;
};

type Rows = { [L in CatalogList]: EditorRow<L>[] };

export type EditMode = "debounce" | "now";

export type UnsavedRow = { list: CatalogList; key: string; label: string };

export type CatalogEditorSummary = {
  saving: number;
  errors: number;
  /** Rows holding a change the server does not have yet — saving or not. */
  unsaved: number;
};

export type CatalogEditor = {
  isReady: boolean;
  rows: Rows;
  settings: CatalogSettings;
  summary: CatalogEditorSummary;
  add: <L extends CatalogList>(list: L, value: CatalogRowValue[L]) => void;
  edit: <L extends CatalogList>(
    list: L,
    key: string,
    patch: Partial<CatalogRowValue[L]>,
    mode: EditMode,
  ) => void;
  /** Saves the row now — blur, Enter, «Reintentar». */
  commit: (list: CatalogList, key: string) => Promise<boolean>;
  /** Puts the row back to what the server holds (or drops a row never created). */
  undo: (list: CatalogList, key: string) => void;
  /** Drops a row that was never created. A saved row goes through `deactivate`. */
  discard: (list: CatalogList, key: string) => void;
  deactivate: (list: CatalogList, key: string) => Promise<boolean>;
  reactivateProduct: (id: string, replacingKey?: string) => Promise<boolean>;
  /** Saves the row's description on its own — the dialog's «Guardar». */
  saveDescription: (key: string, description: string) => Promise<boolean>;
  reorder: (list: CatalogList, orderedKeys: string[]) => void;
  /**
   * Puts a product in a category (or the root, `null`) and saves it at once. By
   * default it lands at the end of that group; a drag passes `orderedKeys`, the
   * whole order with the product where it was dropped.
   */
  moveProduct: (key: string, categoryId: string | null, orderedKeys?: string[]) => Promise<void>;
  setCurrency: (currency: string) => Promise<boolean>;
  setUsesCycles: (usesCycles: boolean) => Promise<boolean>;
  /** Saves everything pending; resolves to what could NOT be saved. */
  flushAll: (options?: { keepalive?: boolean }) => Promise<UnsavedRow[]>;
  /** Field → messages for a row, empty until the row is revealed. */
  fieldErrors: (list: CatalogList, key: string) => Map<string, string[]>;
};

// ── Per-list rules ────────────────────────────────────────────────────────────

const FIELDS: { [L in CatalogList]: (keyof CatalogRowValue[L] & string)[] } = {
  products: ["name", "description", "priceCents", "schedule", "categoryId"],
  cycles: ["key", "label"],
  customFields: ["name", "label", "fieldType", "options"],
  categories: ["name"],
};

/**
 * Fields that only make sense together: a custom field's type and its options
 * are sent as a pair, or a switch to «Opciones» would reach the server with no
 * options and be refused.
 */
const GROUPS: { [L in CatalogList]: string[][] } = {
  products: [],
  cycles: [],
  customFields: [["fieldType", "options"]],
  categories: [],
};

const ROW_SCHEMA = {
  products: catalogProductSchema,
  cycles: catalogCycleSchema,
  customFields: catalogCustomFieldSchema,
  categories: catalogCategorySchema,
} as const;

const LISTS: CatalogList[] = ["categories", "products", "cycles", "customFields"];

/** A row the admin added and never typed into — leaving it behind loses nothing. */
function isBlank(list: CatalogList, value: CatalogRowValue[CatalogList]): boolean {
  if ((list === "products" || list === "categories") && "name" in value) {
    return value.name.trim() === "";
  }
  if (list === "cycles" && "key" in value && "label" in value) {
    return value.key.trim() === "" && value.label.trim() === "";
  }
  if ("name" in value && "label" in value) {
    return value.name.trim() === "" && value.label.trim() === "";
  }
  return false;
}

function rowLabel(list: CatalogList, value: CatalogRowValue[CatalogList]): string {
  const text = list === "cycles" && "key" in value ? value.key : "name" in value ? value.name : "";
  return text.trim() || "(sin nombre)";
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function readField(value: object, field: string): unknown {
  return Object.entries(value).find(([k]) => k === field)?.[1];
}

/** Every validation message on the row, keyed by its top-level field. */
function validate(list: CatalogList, value: object): Map<string, string[]> {
  const parsed = ROW_SCHEMA[list].safeParse({ ...value, id: undefined });
  const errors = new Map<string, string[]>();
  if (parsed.success) return errors;
  for (const issue of parsed.error.issues) {
    const field = String(issue.path[0] ?? "");
    errors.set(field, [...(errors.get(field) ?? []), issue.message]);
  }
  return errors;
}

function changedFields(list: CatalogList, row: EditorRow<CatalogList>): string[] {
  if (!row.saved) return [];
  const saved = row.saved;
  return FIELDS[list].filter(
    (f) => !same(readField(row.current, f), readField(saved, f)),
  );
}

function hasUnsavedChange(list: CatalogList, row: EditorRow<CatalogList>): boolean {
  if (row.id === null) return !isBlank(list, row.current);
  return changedFields(list, row).length > 0;
}

/**
 * The fields of one PATCH: what changed and is valid. A field in a group
 * travels with its partners, and a group with any invalid member waits whole.
 */
function buildPatch(list: CatalogList, row: EditorRow<CatalogList>): Record<string, unknown> | null {
  const errors = validate(list, row.current);
  const changed = new Set(changedFields(list, row));
  const patch: Record<string, unknown> = {};
  const grouped = new Set(GROUPS[list].flat());

  for (const group of GROUPS[list]) {
    if (!group.some((f) => changed.has(f))) continue;
    if (group.some((f) => errors.has(f))) continue;
    for (const f of group) patch[f] = readField(row.current, f);
  }
  for (const f of changed) {
    if (grouped.has(f) || errors.has(f)) continue;
    patch[f] = readField(row.current, f);
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

/** What a create sends — only what a row can hold before it exists. */
function createBody(list: CatalogList, value: object): Record<string, unknown> {
  if (list === "products") {
    return {
      name: readField(value, "name"),
      schedule: readField(value, "schedule") ?? null,
      priceCents: readField(value, "priceCents") ?? null,
      categoryId: readField(value, "categoryId") ?? null,
    };
  }
  if (list === "categories") return { name: readField(value, "name") };
  if (list === "cycles") return { key: readField(value, "key"), label: readField(value, "label") };
  return {
    name: readField(value, "name"),
    label: readField(value, "label"),
    fieldType: readField(value, "fieldType"),
    options: readField(value, "options") ?? null,
  };
}

/**
 * Adopts the server's version of what was SENT — a trimmed name, an emptied
 * description as null — and only for fields the admin has not touched since the
 * request left. A field that was not sent (it was invalid, say) is never
 * replaced, and anything typed during the flight stays and is saved next.
 */
function reconcile<T extends object>(latest: T, sent: Record<string, unknown>, response: object): T {
  const adopted = Object.fromEntries(
    Object.entries(sent)
      .filter(([field, value]) => same(readField(latest, field), value))
      .map(([field]) => [field, readField(response, field)]),
  );
  return { ...latest, ...adopted };
}

let keySeed = 0;
function newKey(): string {
  keySeed += 1;
  return `row-${Date.now().toString(36)}-${keySeed}`;
}

function fromSaved<L extends CatalogList>(value: CatalogRowValue[L] & { id: string }): EditorRow<L> {
  return {
    key: value.id,
    id: value.id,
    saved: value,
    current: value,
    status: "idle",
    error: null,
    errorCode: null,
    revealed: false,
  };
}

function withIds<T extends { id?: string }>(values: T[]): Array<T & { id: string }> {
  return values.flatMap((v) => (v.id ? [{ ...v, id: v.id }] : []));
}

function errorMessage(err: unknown): { message: string; code: string | null } {
  if (err instanceof ApiError) return { message: err.message, code: err.code ?? null };
  return { message: "No se pudo guardar. Revisa tu conexión y vuelve a intentarlo.", code: null };
}

const EMPTY_ROWS: Rows = { products: [], cycles: [], customFields: [], categories: [] };

// ── The hook ─────────────────────────────────────────────────────────────────

export function useCatalogEditor(entry: CatalogEntry | undefined): CatalogEditor {
  const queryClient = useQueryClient();
  const [rows, setRowsState] = useState<Rows>(EMPTY_ROWS);
  const [settings, setSettingsState] = useState<CatalogSettings>({
    currency: "MXN",
    usesCycles: true,
  });
  const [isReady, setIsReady] = useState(false);

  // Async saves need the LATEST rows, not the render they were scheduled in.
  const rowsRef = useRef<Rows>(EMPTY_ROWS);
  const settingsRef = useRef<CatalogSettings>(settings);
  const queues = useRef(new Map<string, Promise<boolean>>());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const setRows = useCallback((update: (prev: Rows) => Rows) => {
    rowsRef.current = update(rowsRef.current);
    setRowsState(rowsRef.current);
  }, []);

  const setSettings = useCallback((next: CatalogSettings) => {
    settingsRef.current = next;
    setSettingsState(next);
  }, []);

  // Hydrate ONCE. A refetch of the catalog (another tab, an invalidation after a
  // save) must never overwrite what the admin is typing.
  useEffect(() => {
    if (!entry || isReady) return;
    const { draft } = entry;
    setRows(() => ({
      // `?? []`: a catalog loaded from a build without categories has none.
      categories: withIds(draft.categories ?? []).map((c) => fromSaved<"categories">(c)),
      products: withIds(draft.products).map((p) => fromSaved<"products">(p)),
      cycles: withIds(draft.cycles).map((c) => fromSaved<"cycles">(c)),
      customFields: withIds(draft.customFields).map((f) => fromSaved<"customFields">(f)),
    }));
    setSettings({ currency: draft.currency, usesCycles: draft.usesCycles });
    setIsReady(true);
  }, [entry, isReady, setRows, setSettings]);

  const findRow = useCallback(
    (list: CatalogList, key: string): EditorRow<CatalogList> | undefined =>
      rowsRef.current[list].find((r) => r.key === key),
    [],
  );

  const patchRow = useCallback(
    (list: CatalogList, key: string, patch: Partial<EditorRow<CatalogList>>) => {
      setRows((prev) => ({
        ...prev,
        [list]: prev[list].map((r) => (r.key === key ? { ...r, ...patch } : r)),
      }));
    },
    [setRows],
  );

  const afterWrite = useCallback(() => {
    // Other screens read the catalog too (Promociones, the lead forms). The
    // editor itself never re-hydrates, so this cannot clobber an edit.
    void queryClient.invalidateQueries({ queryKey: ["catalog"] });
    void queryClient.invalidateQueries({ queryKey: ["catalog-context"] });
  }, [queryClient]);

  /** One save attempt of one row, looping while edits made mid-flight remain. */
  const saveRow = useCallback(
    async (
      list: CatalogList,
      key: string,
      options: { quiet: boolean; keepalive: boolean },
      followUp = 0,
    ): Promise<boolean> => {
      const row = findRow(list, key);
      if (!row) return true;

      if (row.id === null) {
        if (isBlank(list, row.current)) return true;
        if (validate(list, row.current).size > 0) {
          if (!options.quiet) patchRow(list, key, { revealed: true });
          return false;
        }
        const sent = createBody(list, row.current);
        patchRow(list, key, { status: "saving", error: null, errorCode: null });
        try {
          const created = await catalogRowApi.create(list, sent, {
            keepalive: options.keepalive,
          });
          const latest = findRow(list, key);
          if (!latest) return true;
          patchRow(list, key, {
            id: created.id,
            saved: created,
            current: { ...reconcile(latest.current, sent, created), id: created.id },
            status: "saved",
          });
          afterWrite();
        } catch (err) {
          const { message, code } = errorMessage(err);
          if (options.quiet && err instanceof ApiError && err.status === 409) {
            // Mid-typing ("Diseño" on the way to "Diseño Gráfico"): say nothing
            // while the admin is still in the field; the blur retries and shows it.
            patchRow(list, key, { status: "idle" });
            return false;
          }
          patchRow(list, key, { status: "error", error: message, errorCode: code, revealed: true });
          return false;
        }
        return saveRow(list, key, options, followUp + 1);
      }

      const patch = buildPatch(list, row);
      if (!patch) {
        const pending = hasUnsavedChange(list, row);
        if (pending && !options.quiet) patchRow(list, key, { revealed: true });
        return !pending;
      }
      patchRow(list, key, { status: "saving", error: null, errorCode: null });
      try {
        const updated = await catalogRowApi.update(list, row.id, patch, {
          keepalive: options.keepalive,
        });
        const latest = findRow(list, key);
        if (!latest) return true;
        patchRow(list, key, {
          saved: updated,
          current: reconcile(latest.current, patch, updated),
          status: "saved",
        });
        afterWrite();
      } catch (err) {
        const { message, code } = errorMessage(err);
        if (options.quiet && err instanceof ApiError && err.status === 409) {
          patchRow(list, key, { status: "idle" });
          return false;
        }
        patchRow(list, key, { status: "error", error: message, errorCode: code, revealed: true });
        return false;
      }
      const after = findRow(list, key);
      if (!after || !hasUnsavedChange(list, after)) return true;
      // Edits typed during the flight are saved next. Capped, so a server that
      // normalizes a value in a way the row never matches cannot loop forever:
      // the row simply stays pending, and visible as such.
      if (followUp >= MAX_FOLLOW_UP_SAVES) return false;
      return saveRow(list, key, options, followUp + 1);
    },
    [afterWrite, findRow, patchRow],
  );

  const enqueue = useCallback(
    (list: CatalogList, key: string, options: { quiet: boolean; keepalive: boolean }) => {
      const previous = queues.current.get(key);
      const next = (async (): Promise<boolean> => {
        // `saveRow` reports failure as `false` and never throws; the await is
        // only there to keep one row's saves in order.
        if (previous) await previous;
        return saveRow(list, key, options);
      })();
      queues.current.set(key, next);
      return next;
    },
    [saveRow],
  );

  const clearTimer = useCallback((key: string) => {
    const timer = timers.current.get(key);
    if (timer) clearTimeout(timer);
    timers.current.delete(key);
  }, []);

  const commit = useCallback(
    (list: CatalogList, key: string) => {
      clearTimer(key);
      return enqueue(list, key, { quiet: false, keepalive: false });
    },
    [clearTimer, enqueue],
  );

  const edit = useCallback(
    <L extends CatalogList>(
      list: L,
      key: string,
      patch: Partial<CatalogRowValue[L]>,
      mode: EditMode,
    ) => {
      const row = findRow(list, key);
      if (!row) return;
      patchRow(list, key, {
        current: { ...row.current, ...patch },
        // A new keystroke answers the last error; it is re-shown only if the
        // next save fails too.
        ...(row.status === "error" ? { status: "idle", error: null, errorCode: null } : {}),
      });
      if (mode === "now") {
        void commit(list, key);
        return;
      }
      clearTimer(key);
      timers.current.set(
        key,
        setTimeout(() => {
          timers.current.delete(key);
          void enqueue(list, key, { quiet: true, keepalive: false });
        }, AUTOSAVE_DEBOUNCE_MS),
      );
    },
    [clearTimer, commit, enqueue, findRow, patchRow],
  );

  const add = useCallback(
    <L extends CatalogList>(list: L, value: CatalogRowValue[L]) => {
      const row: EditorRow<L> = {
        key: newKey(),
        id: null,
        saved: null,
        current: value,
        status: "idle",
        error: null,
        errorCode: null,
        revealed: false,
      };
      setRows((prev) => ({ ...prev, [list]: [...prev[list], row] }));
    },
    [setRows],
  );

  const undo = useCallback(
    (list: CatalogList, key: string) => {
      clearTimer(key);
      const row = findRow(list, key);
      if (!row) return;
      if (!row.saved) {
        setRows((prev) => ({ ...prev, [list]: prev[list].filter((r) => r.key !== key) }));
        return;
      }
      patchRow(list, key, {
        current: row.saved,
        status: "idle",
        error: null,
        errorCode: null,
        revealed: false,
      });
    },
    [clearTimer, findRow, patchRow, setRows],
  );

  const discard = useCallback(
    (list: CatalogList, key: string) => {
      clearTimer(key);
      setRows((prev) => ({ ...prev, [list]: prev[list].filter((r) => r.key !== key) }));
    },
    [clearTimer, setRows],
  );

  const deactivate = useCallback(
    async (list: CatalogList, key: string): Promise<boolean> => {
      clearTimer(key);
      const row = findRow(list, key);
      if (!row) return true;
      if (row.id === null) {
        discard(list, key);
        return true;
      }
      try {
        await catalogRowApi.deactivate(list, row.id);
      } catch (err) {
        toast.error(errorMessage(err).message);
        return false;
      }
      const categoryId = row.id;
      setRows((prev) => ({
        ...prev,
        [list]: prev[list].filter((r) => r.key !== key),
        // The server moved this category's products to the root in the same
        // transaction; mirror it rather than wait for a reload.
        ...(list === "categories"
          ? {
              products: prev.products.map((r) =>
                r.current.categoryId === categoryId
                  ? {
                      ...r,
                      current: { ...r.current, categoryId: null },
                      saved: r.saved ? { ...r.saved, categoryId: null } : null,
                    }
                  : r,
              ),
            }
          : {}),
      }));
      afterWrite();
      return true;
    },
    [afterWrite, clearTimer, discard, findRow, setRows],
  );

  const reactivateProduct = useCallback(
    async (id: string, replacingKey?: string): Promise<boolean> => {
      let product: CatalogProduct & { id: string };
      try {
        product = await catalogRowApi.reactivateProduct(id);
      } catch (err) {
        toast.error(errorMessage(err).message);
        return false;
      }
      if (replacingKey) clearTimer(replacingKey);
      setRows((prev) => ({
        ...prev,
        // A new row typed with this name was refused for it; the reactivated
        // product takes its place rather than sitting next to a dead copy.
        products: [
          ...prev.products.filter((r) => r.key !== replacingKey),
          fromSaved<"products">(product),
        ],
      }));
      afterWrite();
      return true;
    },
    [afterWrite, clearTimer, setRows],
  );

  const saveDescription = useCallback(
    async (key: string, description: string): Promise<boolean> => {
      const row = findRow("products", key);
      if (!row?.id) return false;
      try {
        const updated = await catalogRowApi.update("products", row.id, { description });
        const latest = findRow("products", key);
        if (latest) {
          patchRow("products", key, {
            saved: { ...(latest.saved ?? updated), description: updated.description },
            current: { ...latest.current, description: updated.description },
          });
        }
        afterWrite();
        return true;
      } catch (err) {
        toast.error(errorMessage(err).message);
        return false;
      }
    },
    [afterWrite, findRow, patchRow],
  );

  const reorder = useCallback(
    (list: CatalogList, orderedKeys: string[]) => {
      const before = rowsRef.current[list];
      const byKey = new Map(before.map((r) => [r.key, r]));
      const next = orderedKeys.flatMap((k) => {
        const row = byKey.get(k);
        return row ? [row] : [];
      });
      setRows((prev) => ({ ...prev, [list]: next }));
      const order = next.flatMap((r, position) => (r.id ? [{ id: r.id, position }] : []));
      if (order.length === 0) return;
      void (async () => {
        try {
          await catalogRowApi.saveOrder({ list, order });
          afterWrite();
        } catch {
          toast.error("No se pudo guardar el orden. Vuelve a intentarlo.");
          setRows((prev) => ({ ...prev, [list]: before }));
        }
      })();
    },
    [afterWrite, setRows],
  );

  const moveProduct = useCallback(
    async (key: string, categoryId: string | null, orderedKeys?: string[]) => {
      setRows((prev) => {
        const row = prev.products.find((r) => r.key === key);
        if (!row) return prev;
        const moved = { ...row, current: { ...row.current, categoryId } };
        const others = prev.products.filter((r) => r.key !== key);
        if (!orderedKeys) {
          // Last in the new group, as the server places it: the products keep
          // one global order and each group shows its own slice of it.
          return { ...prev, products: [...others, moved] };
        }
        const byKey = new Map([...others, moved].map((r) => [r.key, r]));
        return {
          ...prev,
          products: orderedKeys.flatMap((k) => {
            const r = byKey.get(k);
            return r ? [r] : [];
          }),
        };
      });
      const saved = await commit("products", key);
      // The server puts a moved product LAST in its new group. The position it
      // was dropped at is saved AFTER that, or the move would undo it.
      if (saved && orderedKeys) reorder("products", orderedKeys);
    },
    [commit, reorder, setRows],
  );

  const setCurrency = useCallback(
    async (currency: string): Promise<boolean> => {
      const previous = settingsRef.current;
      setSettings({ ...previous, currency });
      try {
        setSettings(await catalogRowApi.updateSettings({ currency }));
        afterWrite();
        return true;
      } catch (err) {
        setSettings(previous);
        toast.error(errorMessage(err).message);
        return false;
      }
    },
    [afterWrite, setSettings],
  );

  const setUsesCycles = useCallback(
    async (usesCycles: boolean): Promise<boolean> => {
      try {
        const next = await catalogRowApi.updateSettings({ usesCycles });
        setSettings(next);
        // The server deactivated every cycle in the same transaction.
        if (!next.usesCycles) {
          for (const row of rowsRef.current.cycles) clearTimer(row.key);
          setRows((prev) => ({ ...prev, cycles: [] }));
        }
        afterWrite();
        return true;
      } catch (err) {
        toast.error(errorMessage(err).message);
        return false;
      }
    },
    [afterWrite, clearTimer, setRows, setSettings],
  );

  const flushAll = useCallback(
    async (options: { keepalive?: boolean } = {}): Promise<UnsavedRow[]> => {
      const keepalive = options.keepalive ?? false;
      // Categories first: a product added inside a new category needs its id.
      const lists = LISTS;
      const attempts: Promise<boolean>[] = [];
      for (const list of lists) {
        for (const row of rowsRef.current[list]) {
          const scheduled = timers.current.has(row.key);
          clearTimer(row.key);
          if (scheduled || hasUnsavedChange(list, row) || row.status === "saving") {
            attempts.push(enqueue(list, row.key, { quiet: false, keepalive }));
          }
        }
      }
      await Promise.all(attempts);
      return lists.flatMap((list) =>
        rowsRef.current[list]
          .filter((row) => hasUnsavedChange(list, row))
          .map((row) => ({ list, key: row.key, label: rowLabel(list, row.current) })),
      );
    },
    [clearTimer, enqueue],
  );

  // Layer 4: a hidden tab may never come back. `visibilitychange` is the signal
  // that fires on iOS too, and the page is still alive when it does, so the
  // session lookup before each request still has time to run.
  useEffect(() => {
    function onVisibilityChange(): void {
      if (document.visibilityState === "hidden") void flushAll({ keepalive: true });
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [flushAll]);

  // Timers must not fire into an unmounted editor.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const summary = useMemo<CatalogEditorSummary>(() => {
    let saving = 0;
    let errors = 0;
    let unsaved = 0;
    for (const list of LISTS) {
      for (const row of rows[list]) {
        if (row.status === "saving") saving += 1;
        if (row.status === "error") errors += 1;
        if (hasUnsavedChange(list, row)) unsaved += 1;
      }
    }
    return { saving, errors, unsaved };
  }, [rows]);

  // Last resort: the tab is closing and something cannot be saved at all (an
  // empty name, half a date range) — let the browser ask.
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent): void {
      if (summary.unsaved === 0) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [summary.unsaved]);

  const fieldErrors = useCallback(
    (list: CatalogList, key: string): Map<string, string[]> => {
      const row = rows[list].find((r) => r.key === key);
      if (!row?.revealed) return new Map();
      return validate(list, row.current);
    },
    [rows],
  );

  return {
    isReady,
    rows,
    settings,
    summary,
    add,
    edit,
    commit,
    undo,
    discard,
    deactivate,
    reactivateProduct,
    saveDescription,
    reorder,
    moveProduct,
    setCurrency,
    setUsesCycles,
    flushAll,
    fieldErrors,
  };
}
